import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 사진 뷰어(라이트박스) — 공유 화면 공통 (기능명세 Z-2-2).
 *
 * ui-core에 대응 컴포넌트가 없어 work-log 로컬로 둔다. 색·반경·z-index는 전부 토큰
 * (D-07 §3.2 #1·#3 — 임의값 금지, z-index는 `--z-modal`).
 *
 * 공유 화면 전용이라 이미지는 `<img src>` 로 직접 로드한다 — 공유 쿠키가 자동 전송되므로
 * blob 로더(ThumbImg)를 쓰면 안 된다.
 */

export interface LightboxPhoto {
  url: string;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_DEFAULT = 1; // 100%
const ZOOM_TOGGLE = 2; // 200% — 1:1 토글 시
const ZOOM_STEP = 0.5; // 확대/축소 버튼 1회당 증감폭

function clampZoom(z: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

/** 두 포인터 사이 거리(px) — 핀치 확대 배율 계산용 */
function pointerDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** url 확장자 추출 — 쿼리스트링 무시. 못 찾으면 jpg로 대체 */
function extOf(url: string): string {
  const m = /\.([a-zA-Z0-9]+)(?:\?.*)?$/.exec(url);
  return m ? m[1] : 'jpg';
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  /** 열린 사진 index. null이면 닫힘 */
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
  /**
   * 이미지 렌더러 override. 미지정 시 `<img src>` 직접 로드(공유쿠키 화면 전용).
   * 인증이 필요한 사장님 화면은 blob 로더(ThumbImg)를 넘겨준다.
   * `style`은 줌 transform이 담긴 스타일 — 반드시 실제 `<img>`에 적용해야 확대/축소가 동작한다
   * (ThumbImg는 `imgStyle` prop 으로 전달).
   */
  renderImage?: (photo: LightboxPhoto, style: React.CSSProperties) => React.ReactNode;
  /** 상단 우측(닫기 버튼 왼쪽) 슬롯 — 인디포토 선택 팝업의 '선택' 체크 토글 등 */
  headerRight?: React.ReactNode;
}

export function PhotoLightbox({ photos, index, onClose, onIndexChange, renderImage, headerRight }: PhotoLightboxProps) {
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);
  // 확대 상태에서 마우스/한손가락으로 이미지 위치 이동(pan). 화면 px 단위 offset.
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null);
  // 활성 포인터(터치) 목록 — 2개가 잡히면 한손가락 드래그 대신 핀치 확대/축소로 전환한다.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinch = useRef<{ startDist: number; startZoom: number } | null>(null);
  // 다운로드 시 화면에 실제 표시 중인 <img>를 찾기 위한 컨테이너 ref.
  // renderImage(blob 로더) / 직접 src 로드 여부와 무관하게 현재 보이는 이미지를 그대로 받는다.
  const imgWrapRef = useRef<HTMLDivElement>(null);

  const open = index !== null && photos.length > 0;

  const nav = useCallback(
    (dir: number) => {
      if (index === null || photos.length === 0) return;
      onIndexChange((index + dir + photos.length) % photos.length);
      setZoom(ZOOM_DEFAULT); // 사진 이동 시 배율 초기화
      setPan({ x: 0, y: 0 });
    },
    [index, photos.length, onIndexChange],
  );

  // 사진 전환·재오픈 시 배율·이동 초기화
  useEffect(() => {
    setZoom(ZOOM_DEFAULT);
    setPan({ x: 0, y: 0 });
  }, [index]);

  // 배율이 100% 이하로 내려가면 이동 불필요 → 초기화
  useEffect(() => {
    if (zoom <= 1) setPan({ x: 0, y: 0 });
  }, [zoom]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') nav(-1);
      else if (e.key === 'ArrowRight') nav(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, nav]);

  if (!open || index === null) return null;

  const stepZoom = (dir: number) => setZoom((z) => clampZoom(z + dir * ZOOM_STEP));

  const handleDownload = () => {
    const imgEl = imgWrapRef.current?.querySelector('img');
    const src = imgEl?.currentSrc || imgEl?.src;
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = `사진_${index + 1}.${extOf(photos[index].url)}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      className="fixed inset-0 flex flex-col"
      style={{ background: 'var(--alpha-black-900)', zIndex: 'var(--z-modal)' }}
      role="dialog"
      aria-modal="true"
      aria-label="사진 보기"
    >
      {/* 상단: 카운터 + 닫기 */}
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-[13px] font-semibold" style={{ color: 'var(--gray-0)' }}>
          {index + 1}/{photos.length}
        </span>
        <span className="flex items-center gap-2">
          {headerRight}
          <IconBtn label="닫기" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </IconBtn>
        </span>
      </div>

      {/*
        이미지 + 좌우 이동. 확대 시 포인터 드래그로 위치 이동(pan).
        모바일 두손가락 핀치 확대/축소 + PC 마우스 휠 확대/축소도 여기서 처리한다.
        touchAction:none 이 브라우저 기본 핀치/스크롤 제스처를 꺼서 아래 로직이 그대로 받는다.
      */}
      <div
        ref={imgWrapRef}
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        style={{
          cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
          touchAction: 'none',
          userSelect: 'none',
        }}
        onPointerDown={(e) => {
          // 좌우 이동/줌 버튼 클릭은 드래그·핀치로 가로채지 않는다
          if ((e.target as HTMLElement).closest('button')) return;
          pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          e.currentTarget.setPointerCapture(e.pointerId);

          if (pointers.current.size === 2) {
            // 두번째 손가락이 닿는 순간 — 진행 중이던 한손가락 드래그를 접고 핀치로 전환
            drag.current = null;
            setDragging(false);
            const [a, b] = [...pointers.current.values()];
            pinch.current = { startDist: pointerDistance(a, b), startZoom: zoom };
            return;
          }

          if (zoom <= 1) return;
          e.preventDefault();
          drag.current = { sx: e.clientX, sy: e.clientY, bx: pan.x, by: pan.y };
          setDragging(true);
        }}
        onPointerMove={(e) => {
          if (pointers.current.has(e.pointerId)) {
            pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
          }

          if (pointers.current.size === 2 && pinch.current) {
            const [a, b] = [...pointers.current.values()];
            const scale = pointerDistance(a, b) / pinch.current.startDist;
            setZoom(clampZoom(pinch.current.startZoom * scale));
            return;
          }

          if (!drag.current) return;
          setPan({ x: drag.current.bx + (e.clientX - drag.current.sx), y: drag.current.by + (e.clientY - drag.current.sy) });
        }}
        onPointerUp={(e) => {
          pointers.current.delete(e.pointerId);
          if (pointers.current.size < 2) pinch.current = null;
          drag.current = null;
          setDragging(false);
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        }}
        onPointerCancel={(e) => {
          pointers.current.delete(e.pointerId);
          pinch.current = null;
          drag.current = null;
          setDragging(false);
        }}
        onWheel={(e) => {
          // PC 마우스 휠로 확대/축소 — 습관적으로 스크롤하는 동작을 그대로 줌으로 받는다(페이지 스크롤 없음, 전체화면 모달).
          e.preventDefault();
          setZoom((z) => clampZoom(z * (1 - e.deltaY * 0.001)));
        }}
      >
        {(() => {
          const style: React.CSSProperties = {
            maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transition: dragging ? 'none' : 'transform .15s',
          };
          return renderImage ? (
            renderImage(photos[index], style)
          ) : (
            <img src={photos[index].url} alt={`사진 ${index + 1}`} style={style} draggable={false} />
          );
        })()}
        {photos.length > 1 && (
          <>
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <IconBtn label="이전 사진" onClick={() => nav(-1)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </IconBtn>
            </div>
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <IconBtn label="다음 사진" onClick={() => nav(1)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </IconBtn>
            </div>
          </>
        )}
      </div>

      {/* 하단: 확대/축소 */}
      <div className="flex items-center justify-center gap-2 py-4">
        <IconBtn label="축소" onClick={() => stepZoom(-1)} disabled={zoom <= ZOOM_MIN}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </IconBtn>
        <span className="w-14 text-center text-body-sm font-semibold" style={{ color: 'var(--gray-0)' }}>
          {Math.round(zoom * 100)}%
        </span>
        <IconBtn label="확대" onClick={() => stepZoom(1)} disabled={zoom >= ZOOM_MAX}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </IconBtn>
        <IconBtn
          label="1:1 맞춤"
          onClick={() => setZoom((z) => (z === ZOOM_DEFAULT ? ZOOM_TOGGLE : ZOOM_DEFAULT))}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </IconBtn>
        <IconBtn label="다운로드" onClick={handleDownload}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" /><polyline points="7 10 12 15 17 10" /><path d="M5 21h14" />
          </svg>
        </IconBtn>
      </div>
    </div>
  );
}

/**
 * 라이트박스 전용 아이콘 버튼. ui-core `Button` 을 쓰지 않는 이유 — Button은 shell/group
 * 토큰 기반이라 어두운 오버레이 위에서 대비가 무너진다. 터치타겟 48×48 유지(D-07 §3.3).
 */
function IconBtn({
  children, label, onClick, disabled,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center"
      style={{
        width: 48, height: 48, borderRadius: 'var(--radius-full)', border: 'none',
        background: 'var(--alpha-white-100)', color: 'var(--gray-0)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
      }}
    >
      {children}
    </button>
  );
}
